import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLines } from "../motion/Reveal";
import { ArrowUpRight, Sparkles } from "lucide-react";

const HERO_IMG =
    "https://images.unsplash.com/photo-1760344594784-60ff14035eb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxncmVlbiUyMHRlY2glMjBhYnN0cmFjdHxlbnwwfHx8fDE3ODQ3MjkzNDR8MA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
    const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
    const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);

    return (
        <section
            ref={ref}
            id="top"
            className="relative min-h-screen w-full overflow-hidden hero-aura"
            data-testid="hero-section"
        >
            {/* Grid backdrop */}
            <div className="absolute inset-0 gd-gridlines opacity-40 pointer-events-none" />

            {/* Parallax background image on right side (clipped) */}
            <motion.div
                style={{ y: imgY, scale: imgScale }}
                className="absolute top-[10%] right-[-6%] w-[55%] h-[70%] hidden lg:block clip-frame overflow-hidden opacity-70"
            >
                <img
                    src={HERO_IMG}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-transparent to-transparent" />
            </motion.div>

            {/* Top status bar */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.8 }}
                className="absolute top-[84px] left-4 right-4 md:top-[92px] md:left-10 md:right-auto flex items-center gap-3 text-[10px] sm:text-xs font-mono-tech text-white/50 leading-relaxed"
            >
                <span className="w-1.5 h-1.5 rounded-full bg-[#00FF66] animate-pulse" />
                <span>DISPONÍVEL PARA NOVOS PROJETOS · Q4 2026</span>
            </motion.div>

            <motion.div
                style={{ y: textY }}
                className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6 md:px-10 pt-[160px] sm:pt-[180px] md:pt-[220px] pb-24 md:pb-32"
            >
                <div className="flex items-center gap-3 mb-6 md:mb-8">
                    <motion.span
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4, duration: 0.6 }}
                        className="text-[#00FF66] font-mono-tech text-[10px] sm:text-xs tracking-widest uppercase flex items-center gap-2"
                    >
                        <span className="w-6 sm:w-8 h-[1px] bg-[#00FF66]" />
                        Estúdio de tecnologia · Brasil
                    </motion.span>
                </div>

                <MaskedLines
                    as="h1"
                    lines={["Construímos", "o software", "que sua"]}
                    className="font-display uppercase text-[15vw] sm:text-[12vw] md:text-[8.5vw] lg:text-[7.25vw] font-medium leading-[0.88] md:leading-[0.9] tracking-tighter text-white"
                    delay={0.2}
                    stagger={0.14}
                />
                <div className="mt-1 md:mt-2 flex items-baseline gap-4 flex-wrap">
                    <MaskedLines
                        as="h1"
                        lines={["empresa merece."]}
                        className="font-display uppercase text-[15vw] sm:text-[12vw] md:text-[8.5vw] lg:text-[7.25vw] font-medium leading-[0.88] md:leading-[0.9] tracking-tighter text-[#00FF66]"
                        delay={0.6}
                    />
                </div>

                <div className="mt-10 md:mt-16 grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10 lg:gap-16 items-end">
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.1, duration: 0.9 }}
                        className="text-white/70 text-base sm:text-lg leading-relaxed max-w-md lg:col-span-1"
                    >
                        Apps, sites, automação e inteligência artificial —
                        desenhados como produto, entregues como um relógio
                        suíço. Sem gambiarras. Sem enrolação.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.3, duration: 0.9 }}
                        className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4"
                    >
                        <a
                            href="#contato"
                            className="btn-neon justify-center sm:justify-start"
                            data-testid="hero-cta-primary"
                        >
                            <Sparkles className="w-4 h-4" strokeWidth={2.2} />
                            Iniciar um projeto
                        </a>
                        <a
                            href="#trabalhos"
                            className="btn-ghost justify-center sm:justify-start"
                            data-testid="hero-cta-secondary"
                        >
                            Ver trabalhos
                            <ArrowUpRight className="w-4 h-4" />
                        </a>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.4, duration: 0.9 }}
                        className="text-left lg:text-right lg:justify-self-end flex flex-col gap-1"
                    >
                        <span className="font-display text-5xl text-white">
                            +80
                        </span>
                        <span className="font-mono-tech text-xs uppercase tracking-widest text-white/50">
                            produtos digitais entregues
                        </span>
                    </motion.div>
                </div>
            </motion.div>

            {/* Scroll cue */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 1 }}
                className="absolute bottom-6 left-4 md:bottom-8 md:left-10 hidden sm:flex items-center gap-3 text-xs font-mono-tech text-white/40"
            >
                <span className="w-[1px] h-10 bg-white/30 relative overflow-hidden">
                    <motion.span
                        className="absolute inset-x-0 top-0 h-4 bg-[#00FF66]"
                        animate={{ y: [-16, 40] }}
                        transition={{
                            repeat: Infinity,
                            duration: 2.2,
                            ease: "easeInOut",
                        }}
                    />
                </span>
                ROLE PARA EXPLORAR
            </motion.div>
        </section>
    );
}
